import { useEffect, useState } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { ShieldCheck } from "lucide-react"
import {
  ensureSession,
  signOut,
  updateDisplayName,
  type SessionUser,
} from "@/core/auth"
import {
  Card,
  Field,
  GhostButton,
  Pill,
  PrimaryButton,
  SettingsPage,
  TextInput,
} from "@/components/settings/primitives"

// The signed-in admin's own account. User-level (not event-scoped) data, but it
// lives under the event shell so it renders in the canvas with the same chrome
// as Settings. Name is editable via Better Auth's update-user; email + role are
// read-only here.
export const Route = createFileRoute("/$orgSlug/$eventSlug/profile")({
  component: ProfileView,
})

function ProfileView() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const q = useQuery({
    queryKey: ["session", "me"],
    queryFn: () => ensureSession(),
    staleTime: 30_000,
  })
  const user = q.data ?? null

  const [name, setName] = useState("")
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!user || dirty) return
    setName(user.name ?? "")
  }, [user, dirty])

  const save = useMutation({
    mutationFn: () => updateDisplayName(name.trim()),
    onSuccess: () => {
      setDirty(false)
      toast.success("Profile saved")
      void qc.invalidateQueries({ queryKey: ["session", "me"] })
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to save profile"),
  })

  const out = useMutation({
    mutationFn: () => signOut(),
    onSuccess: () => {
      qc.clear()
      void navigate({ to: "/sign-in", search: { next: "/" } })
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to sign out"),
  })

  const canSave = dirty && !!name.trim() && name.trim() !== (user?.name ?? "") && !save.isPending

  return (
    <SettingsPage
      title="Profile"
      description="Your Attend account. This is who you are across every event you organize."
      actions={
        <PrimaryButton onClick={() => save.mutate()} disabled={!canSave}>
          {save.isPending ? "Saving…" : "Save changes"}
        </PrimaryButton>
      }
    >
      {q.isError && (
        <Card>
          <p className="text-[13px] text-rose-600 dark:text-rose-400">
            {(q.error as Error)?.message ?? "Failed to load your profile"}
          </p>
        </Card>
      )}

      <Card title="Identity">
        <div className="flex items-center gap-4">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-amber-300 to-orange-500 text-[18px] font-bold text-plum-950">
            {initialsOf(user)}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[15px] font-semibold text-plum-900 dark:text-plum-50">
              {user?.name?.trim() || "Unnamed"}
            </p>
            <p className="truncate text-[12.5px] text-plum-700/60 dark:text-plum-200/45">
              {user?.email ?? (q.isLoading ? "Loading…" : "—")}
            </p>
          </div>
        </div>

        <Field label="Display name" hint="Shown to your team and on the messages you send.">
          <TextInput
            value={name}
            disabled={!user || save.isPending}
            placeholder="Your name"
            onChange={(e) => {
              setDirty(true)
              setName(e.target.value)
            }}
          />
        </Field>

        <Field label="Email">
          <div className="flex items-center gap-2.5">
            <TextInput value={user?.email ?? ""} disabled readOnly className="flex-1" />
            {user?.emailVerified ? (
              <Pill tone="accent">
                <span className="inline-flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" />
                  Verified
                </span>
              </Pill>
            ) : (
              <Pill tone="amber">Unverified</Pill>
            )}
          </div>
        </Field>

        {user?.role && (
          <Field label="Platform role">
            <Pill>{user.role}</Pill>
          </Field>
        )}
      </Card>

      <Card
        title="Session"
        description="Signed in on this device via your Attend cross-event login."
      >
        <GhostButton
          onClick={() => out.mutate()}
          disabled={out.isPending}
          className="text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10"
        >
          {out.isPending ? "Signing out…" : "Sign out"}
        </GhostButton>
      </Card>
    </SettingsPage>
  )
}

function initialsOf(user: SessionUser | null): string {
  const name = user?.name?.trim()
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean)
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
  }
  const email = user?.email?.trim()
  if (email) return email.slice(0, 2).toUpperCase()
  return "·"
}
